/**
 * SIMPLE-1 (2026-10-13): the main tool shows ONE list with two labels and two money figures.
 *
 * Coen: the card carried a shortlist score, an evidence score, a tier, a comparables count, a
 * gross and a net margin, a rarity label and four start routes. Each number was defensible on its
 * own; together nobody could read them. What an importer needs is: is it a pick, what do I make,
 * what do I make if the sale disappoints, and where is the advert. That is all this module answers.
 *
 *   koopkans    every check passed AND the dealer page confirmed the car < 24 h ago
 *   controleer  the model passed it, but the dealer page could not be read (or the check expired),
 *               or it loses more than NET_LOSS_LIMIT_EUR if the sale disappoints (T4C-NET-1)
 *   geen        not a pick; the verdict's reason says why
 */
import { UNVERIFIED_CANDIDATE_CLAUSE } from './unverifiedCandidates.js'
import { SERVED_CONTRACT, formatUtcForPocketBase } from '../contracts/servedRecommendation.v1.js'

/**
 * "Controleer zelf": the model passed the car, but the dealer page was not confirmed live in the
 * last 24 hours -- it could not be read (the source-verification demotion), or the last live
 * check has expired. Exactly the complement of the contract's recommendation surface within the
 * model-approved cars, so a card's label always matches the section it is listed in.
 */
export function checkYourselfClause(now = new Date()) {
  const hours = Number(SERVED_CONTRACT.recommendation_surface.source_check_max_age_hours)
  const cutoff = formatUtcForPocketBase(new Date(new Date(now).getTime() - hours * 3600000))
  return `(${UNVERIFIED_CANDIDATE_CLAUSE} || buy_score > 0 && (source_flag != 'live' || source_checked_at < '${cutoff}' || ${NET_LOSS_CLAUSE}))`
}

/**
 * T4C-NET-1 (Coen, 2026-10-14): a pick that loses more than this when it only sells at the low end
 * of its published band, after the 4% sale spread and the condition reserve (`profit_safe_eur`),
 * is Controleer zelf with that loss as its reason, not a Koopkans. The pipeline's T4c gate reads
 * the GROSS low end (`downside_eur < -1000`); 17 of 196 cohort picks (Alfa 2 of 8) passed it and
 * still lost more net. Shortcut: the app applies it on top of the contract's recommendation
 * surface; fold it into that surface at the next contract version (see CONTRACT-SORT-1).
 */
export const NET_LOSS_LIMIT_EUR = 1000
export const NET_LOSS_CLAUSE = `profit_safe_eur < -${NET_LOSS_LIMIT_EUR}`
export const NET_LOSS_OK_CLAUSE = `profit_safe_eur >= -${NET_LOSS_LIMIT_EUR}`

/** verdict key -> one of the three kinds the card shows. */
export function pickKind(verdictKey) {
  if (verdictKey === 'recommended') return 'koopkans'
  if (verdictKey === 'unverified' || verdictKey === 'downside') return 'controleer'
  return 'geen'
}

const num = value => (value === null || value === undefined || value === '' ? null
  : Number.isFinite(Number(value)) ? Number(value) : null)

/** Net profit the decision used (after the 4% sale spread and the condition reserve), at the
 * point value and at the low end of the published 80% band. Prefers the stored fields so the
 * card shows exactly the number the list was sorted on; older releases lack them, so the same
 * formula as profit.py recomputes them from served fields. */
export function profitFigures(car) {
  const stored = { expected: num(car?.profit_expected_eur), safe: num(car?.profit_safe_eur) }
  // PocketBase stores an unset number as 0, so 0 and 0 together means "not written yet".
  if (stored.expected !== null && stored.safe !== null && (stored.expected !== 0 || stored.safe !== 0)) return stored
  const { expected, safe } = netProfit(car, num(car?.expected_revenue))
  return { expected, safe }
}

/** profit.py's net figures from a gross margin (the P&L passes its incl./excl. VAT one): minus the
 * 4% sale spread and the condition reserve; at the low end of the band 96% of the gap is lost,
 * because the spread shrinks with the price. */
export function netProfit(car, revenue) {
  const point = num(car?.pred_target_price), low = num(car?.pred_target_price_lower_80)
  if (revenue === null || revenue === undefined || point === null || point <= 0) {
    return { expected: null, safe: null, haircut: null, reserve: null }
  }
  // A real sale spread is always > 0 when there is a value; 0 is the unset default.
  const haircut = num(car.sale_haircut_eur) || 0.04 * point
  const reserve = num(car.condition_reserve_eur) ?? 0
  const expected = revenue - haircut - reserve
  return { expected, safe: low === null ? null : expected - 0.96 * (point - low), haircut, reserve }
}

const hostOf = url => { try { return new URL(url).hostname.replace(/^www\./, '') } catch { return null } }
const isHttp = url => typeof url === 'string' && /^https?:\/\//i.test(url) && hostOf(url) !== null

/** The one outbound link: the dealer's own advert when we resolved it, else AutoUncle's page
 * (whose /tot-ziens/ redirect lands on the dealer; a /d/ page is hosted by AutoUncle itself).
 * Only http(s) URLs are ever returned. */
export function adLink(car) {
  const real = car?.source_real_url
  if (isHttp(real) && !/(^|\.)autouncle\./i.test(hostOf(real))) {
    return { href: real, host: hostOf(real), direct: true }
  }
  const fallback = [car?.detail_page_url, car?.external_link].find(isHttp)
  return fallback ? { href: fallback, host: hostOf(fallback), direct: false } : null
}

const FUEL_SLUG = { benzine: 'petrol', petrol: 'petrol', diesel: 'diesel', electric: 'electric',
  elektrisch: 'electric', hybrid: 'hybrid', hybride: 'hybrid', lpg: 'lpg' }

/** AutoUncle.nl search for the same car: same model and fuel, +/-2 years, +/-25k km, +/-50 hp. */
export function nlComparablesUrl(car, thisYear = new Date().getFullYear()) {
  const year = num(car?.year) || thisYear
  const km = num(car?.mileage_km) ?? num(car?.mileage) ?? 0
  const hp = num(car?.horsepower_hp) ?? num(car?.power) ?? 0
  const fuel = FUEL_SLUG[String(car?.fuel_type ?? car?.fuel ?? '').toLowerCase()]
  const path = [car?.make, car?.model].map(part => encodeURIComponent(part || '')).join('/')
  const params = new URLSearchParams({
    's[min_km]': String(Math.max(0, km - 25000)), 's[max_km]': String(km + 25000),
    's[min_year]': String(year - 2), 's[max_year]': String(Math.min(thisYear + 1, year + 2)),
  })
  if (hp > 0) {
    params.set('s[min_hp]', String(Math.max(0, Math.round(hp - 50))))
    params.set('s[max_hp]', String(Math.round(hp + 50)))
  }
  return `https://www.autouncle.nl/nl-nl/gebruikte-auto/${path}${fuel ? `/f-${fuel}` : ''}?${params}`
}
